import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { APP } from '@/constants/testIds';
import {
    CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator
} from '@/components/ui/command';
import { House, HouseLine, ClipboardText, PaperPlaneTilt, User, TestTube } from '@phosphor-icons/react';

export default function CommandPalette() {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(false);
    const nav = useNavigate();

    useEffect(() => {
        const down = (e) => {
            if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                setOpen((o) => !o);
            }
            if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey) {
                const el = document.activeElement;
                const tag = el?.tagName?.toLowerCase();
                if (tag !== 'input' && tag !== 'textarea' && !el?.isContentEditable) {
                    e.preventDefault();
                    setOpen(true);
                }
            }
        };
        window.addEventListener('keydown', down);
        return () => window.removeEventListener('keydown', down);
    }, []);

    useEffect(() => {
        if (!open) return;
        let cancel = false;
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get('/patients', { params: q ? { search: q } : {} });
                if (!cancel) setPatients(data.slice(0, 20));
            } catch { if (!cancel) setPatients([]); }
            finally { if (!cancel) setLoading(false); }
        }, 150);
        return () => { cancel = true; clearTimeout(t); };
    }, [q, open]);

    const go = (path) => { setOpen(false); nav(path); };

    return (
        <>
            <button
                data-testid={APP.searchTrigger}
                onClick={() => setOpen(true)}
                className="hidden md:flex items-center gap-2 mx-3 my-3 px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-500 hover:border-primary hover:text-primary transition-colors duration-100"
            >
                <span className="flex-1 text-left">Search family members…</span>
                <kbd className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-500">⌘K</kbd>
            </button>

            <CommandDialog open={open} onOpenChange={setOpen}>
                <CommandInput
                    data-testid={APP.searchInput}
                    value={q}
                    onValueChange={setQ}
                    placeholder="Search family members, jump to pages…"
                />
                <CommandList>
                    <CommandEmpty>{loading ? 'Searching…' : 'No results.'}</CommandEmpty>

                    {patients.length > 0 && (
                        <CommandGroup heading="Family members">
                            {patients.map(p => (
                                <CommandItem
                                    key={p.patient_id}
                                    data-testid={APP.searchResult}
                                    value={`${p.name} ${p.facility_name || ''}`}
                                    onSelect={() => go(`/members/${p.patient_id}`)}
                                >
                                    <User size={16} className="mr-2 text-primary" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-slate-900">{p.name}</div>
                                        <div className="text-[11px] text-slate-500">
                                            {p.facility_name || '—'}
                                            {p.open_items > 0 && ` · ${p.open_items} open`}
                                        </div>
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    )}

                    <CommandSeparator />
                    <CommandGroup heading="Navigate">
                        <CommandItem onSelect={() => go('/dashboard')}><House size={16} className="mr-2" /> Home</CommandItem>
                        <CommandItem onSelect={() => go('/households')}><HouseLine size={16} className="mr-2" /> Households</CommandItem>
                        <CommandItem onSelect={() => go('/check-ins')}><ClipboardText size={16} className="mr-2" /> Check-ins</CommandItem>
                        <CommandItem onSelect={() => go('/sent-check-ins')}><PaperPlaneTilt size={16} className="mr-2" /> Sent check-ins</CommandItem>
                        <CommandItem onSelect={() => go('/settings/lab-tests')}><TestTube size={16} className="mr-2" /> Lab tests</CommandItem>
                    </CommandGroup>
                </CommandList>
                <div className="border-t border-slate-200 px-3 py-2 text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-3">
                    <span>↑↓ navigate</span>
                    <span>↵ open</span>
                    <span>Esc close</span>
                    <span className="ml-auto">Press / anywhere to search</span>
                </div>
            </CommandDialog>
        </>
    );
}
